import useTranslation from '../useTranslation';
import type { BoundaryPartRequest } from 'generatedTypes/flight/models';
import type { Feature, MultiPolygon, Polygon } from 'geojson';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { computeBoundaryFromZones } from 'utils/boundaryFromZones';
import { storePrescriptionId } from 'utils/prescriptionStorage';
import { finalizeParse, parseShapefile } from 'utils/shapefileParser';

import useApplication from 'hooks/useApplication';
import useBoundary from 'hooks/useBoundary';
import usePageRoutes from 'hooks/usePageRoutes';
import usePrescription from 'hooks/usePrescription';

import {
    BOUNDARY_NAME_PREFIX,
    BOUNDARY_TYPE_APPLY,
    DEFAULT_GAP_POLICY,
    DEFAULT_RATE_UNIT,
    GEOMETRY_JSON_TYPE_POLYGON,
    GEOMETRY_TYPE_POLYGON
} from './constants';
import type {
    ColumnSelectedHandler,
    FilesSelectedHandler,
    GapPolicyChangeHandler,
    PrescriptionGapPolicy,
    ShapefileParseError,
    UploadState,
    UseDataReturn
} from './types';

// Rings of [longitude, latitude] captured exactly as GeoJSON serializes them.
// The generated BoundaryPartRequest claims JTS-style fields but the wire shape
// is GeoJSON rings — matches the existing `as unknown as BoundaryPartRequest`
// cast in useJohnDeereImport/buildBoundaryParts.
type GeoJsonPolygonRings = number[][][];

type WireBoundaryPart = {
    boundary: { coordinates: GeoJsonPolygonRings; type: typeof GEOMETRY_JSON_TYPE_POLYGON };
    boundaryType: typeof BOUNDARY_TYPE_APPLY;
    geometryType: typeof GEOMETRY_TYPE_POLYGON;
};

const featureToBoundaryParts = (feature: Feature<Polygon | MultiPolygon>): WireBoundaryPart[] => {
    const { geometry } = feature;

    if (geometry.type === GEOMETRY_JSON_TYPE_POLYGON) {
        return [
            {
                boundary: {
                    coordinates: geometry.coordinates as GeoJsonPolygonRings,
                    type: GEOMETRY_JSON_TYPE_POLYGON
                },
                boundaryType: BOUNDARY_TYPE_APPLY,
                geometryType: GEOMETRY_TYPE_POLYGON
            }
        ];
    }

    return geometry.coordinates.map(polygonRings => ({
        boundary: {
            coordinates: polygonRings as GeoJsonPolygonRings,
            type: GEOMETRY_JSON_TYPE_POLYGON
        },
        boundaryType: BOUNDARY_TYPE_APPLY,
        geometryType: GEOMETRY_TYPE_POLYGON
    }));
};

const isInvalidPolygonsError = (error: ShapefileParseError | undefined): boolean => {
    if (!error) {
        return false;
    }

    return error.kind === 'invalidPolygons' || error.kind === 'unsupportedCrs';
};

const pickFileName = (files: File[]): string => {
    const zipFile = files.find(file => file.name.toLowerCase().endsWith('.zip'));

    if (zipFile) {
        return zipFile.name;
    }
    const shpFile = files.find(file => file.name.toLowerCase().endsWith('.shp'));

    if (shpFile) {
        return shpFile.name.replace(/\.shp$/i, '');
    }

    return files[0]?.name ?? '';
};

const useData = (): UseDataReturn => {
    const {
        ERROR_APPLICATION_FAILED,
        ERROR_BOUNDARY_FAILED,
        ERROR_CRS,
        ERROR_INVALID_POLYGONS,
        ERROR_PARSE_FAILED,
        ERROR_PRESCRIPTION_FAILED
    } = useTranslation();

    const { createApplication } = useApplication();
    const { createBoundary } = useBoundary();
    const { createPrescription } = usePrescription();
    const { applicationDetailsPage, applicationsPage } = usePageRoutes();
    const navigate = useNavigate();

    const [uploadState, setUploadState] = useState<UploadState>({ kind: 'idle' });
    const [gapPolicy, setGapPolicy] = useState<PrescriptionGapPolicy>(DEFAULT_GAP_POLICY);
    const [pendingFileName, setPendingFileName] = useState<string>('');

    const handleFilesSelected: FilesSelectedHandler = useCallback(
        async ({ files }) => {
            if (files.length === 0) {
                return;
            }

            setPendingFileName(pickFileName(files));
            setUploadState({ kind: 'parsing' });

            const zipFile = files.find(file => file.name.toLowerCase().endsWith('.zip'));
            const result = await parseShapefile({
                input: zipFile ? { zip: zipFile } : { files }
            });

            if (!result.ok) {
                const { error } = result;
                let message = ERROR_PARSE_FAILED('unknown error');

                if (error.kind === 'missingFile') {
                    message = ERROR_PARSE_FAILED(`missing .${error.file} file`);
                } else if (error.kind === 'emptyShapefile') {
                    message = ERROR_PARSE_FAILED('shapefile contains no features');
                } else if (error.kind === 'shpjsFailure') {
                    message = ERROR_PARSE_FAILED(error.detail);
                } else if (error.kind === 'invalidPolygons') {
                    message = ERROR_INVALID_POLYGONS(error.count);
                } else if (error.kind === 'unsupportedCrs') {
                    message = ERROR_CRS(error.detected);
                }

                setUploadState({ kind: 'errored', message, parseError: error });

                return;
            }

            setUploadState({ kind: 'columnSelection', staged: result.staged });
        },
        [ERROR_CRS, ERROR_INVALID_POLYGONS, ERROR_PARSE_FAILED]
    );

    const handleColumnSelected: ColumnSelectedHandler = useCallback(
        ({ rateColumn }) => {
            if (uploadState.kind !== 'columnSelection') {
                return;
            }

            setUploadState({ kind: 'finalizing' });
            const finalized = finalizeParse({
                rateColumn,
                staged: uploadState.staged
            });

            if (!finalized.ok) {
                const { error } = finalized;
                let message = ERROR_PARSE_FAILED('unknown error');

                if (error.kind === 'invalidPolygons') {
                    message = ERROR_INVALID_POLYGONS(error.count);
                } else if (error.kind === 'unsupportedCrs') {
                    message = ERROR_CRS(error.detected);
                } else if (error.kind === 'emptyShapefile') {
                    message = ERROR_PARSE_FAILED('shapefile contains no features');
                } else if (error.kind === 'shpjsFailure') {
                    message = ERROR_PARSE_FAILED(error.detail);
                }

                setUploadState({ kind: 'errored', message, parseError: error });

                return;
            }

            setUploadState({
                fileName: pendingFileName,
                kind: 'readyToSubmit',
                rateColumn,
                zones: finalized.zones
            });
        },
        [ERROR_CRS, ERROR_INVALID_POLYGONS, ERROR_PARSE_FAILED, pendingFileName, uploadState]
    );

    const handleCancelUpload = useCallback(() => {
        setUploadState({ kind: 'idle' });
    }, []);

    const handleCancelPage = useCallback(() => {
        navigate(applicationsPage);
    }, [applicationsPage, navigate]);

    const handleWarningDismiss = useCallback(() => {
        setUploadState({ kind: 'idle' });
    }, []);

    const handleGapPolicyChange: GapPolicyChangeHandler = useCallback(({ gapPolicy: next }) => {
        setGapPolicy(next);
    }, []);

    const handleSubmit = useCallback(async () => {
        if (uploadState.kind !== 'readyToSubmit') {
            return;
        }

        const { zones } = uploadState;
        const boundaryFeature = computeBoundaryFromZones(zones);

        if (!boundaryFeature) {
            setUploadState({ kind: 'errored', message: ERROR_BOUNDARY_FAILED });

            return;
        }

        try {
            setUploadState({ kind: 'creating' });

            const boundaryParts = featureToBoundaryParts(boundaryFeature);
            const boundaryResponse = await createBoundary({
                boundary: {
                    boundaryParts: boundaryParts as unknown as BoundaryPartRequest[],
                    name: `${BOUNDARY_NAME_PREFIX} ${new Date().toISOString()}`
                }
            });

            if (!boundaryResponse) {
                setUploadState({ kind: 'errored', message: ERROR_BOUNDARY_FAILED });

                return;
            }

            const applicationResponse = await createApplication({
                application: {
                    batteryIds: [],
                    boundaryId: boundaryResponse.id,
                    droneIds: [],
                    scheduledPilotIds: [],
                    status: 'NOT_STARTED'
                }
            });

            if (!applicationResponse) {
                setUploadState({ kind: 'errored', message: ERROR_APPLICATION_FAILED });

                return;
            }

            try {
                const prescriptionResponse = await createPrescription({
                    prescription: { gapPolicy, rateUnit: DEFAULT_RATE_UNIT, zones }
                });

                if (prescriptionResponse) {
                    storePrescriptionId({
                        applicationId: applicationResponse.id,
                        prescriptionId: prescriptionResponse.id
                    });
                } else {
                    console.warn(ERROR_PRESCRIPTION_FAILED);
                }
            } catch (prescriptionError) {
                console.warn(ERROR_PRESCRIPTION_FAILED, prescriptionError);
            }

            navigate(applicationDetailsPage(applicationResponse.id), {
                state: { autoOpenPlanner: true }
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : ERROR_APPLICATION_FAILED;

            setUploadState({ kind: 'errored', message });
        }
    }, [
        ERROR_APPLICATION_FAILED,
        ERROR_BOUNDARY_FAILED,
        ERROR_PRESCRIPTION_FAILED,
        applicationDetailsPage,
        createApplication,
        createBoundary,
        createPrescription,
        gapPolicy,
        navigate,
        uploadState
    ]);

    const columns = uploadState.kind === 'columnSelection' ? uploadState.staged.columns : [];
    const isColumnPickerOpen = uploadState.kind === 'columnSelection';
    const isProcessing = uploadState.kind === 'parsing' || uploadState.kind === 'finalizing';
    const isSubmitting = uploadState.kind === 'creating';
    const isWarningOpen =
        uploadState.kind === 'errored' && isInvalidPolygonsError(uploadState.parseError);
    const errorMessage =
        uploadState.kind === 'errored' && !isInvalidPolygonsError(uploadState.parseError)
            ? uploadState.message
            : null;
    const warningMessage =
        uploadState.kind === 'errored' && isInvalidPolygonsError(uploadState.parseError)
            ? uploadState.message
            : '';

    return {
        columns,
        errorMessage,
        gapPolicy,
        handleCancelPage,
        handleCancelUpload,
        handleColumnSelected,
        handleFilesSelected,
        handleGapPolicyChange,
        handleSubmit,
        handleWarningDismiss,
        isColumnPickerOpen,
        isProcessing,
        isSubmitting,
        isWarningOpen,
        uploadState,
        warningMessage
    };
};

export default useData;
